import CommunicationsService from '@/services/CommunicationsService'
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  XCircleIcon
} from '@heroicons/vue/24/outline'

const state = () => ({
  scope: 'user',
  raw: [],
  loading: false,
  error: null,
  message: null,

  filters: {
    search: '',
    date: '',
    dateTo: '',
    type: 'all'
  },

  pageSize: 10,
  currentPage: 1,

  voicemail: {
    raw: [],
    loading: false,
    error: null,
    message: null,
    selectedId: null,
    audioUrlById: {},
    audioLoading: false
  }
})

const getters = {
  uiRows (s, g, rootState) {
    const tz = rootState.auth?.timezone || 'America/Phoenix'
    const raw = Array.isArray(s.raw) ? s.raw : []
    const deduped = dedupeCdrRows(raw)
    return deduped.map(r => mapCdrToUiRow(r, tz))
  },

  filteredUiRows (s, g) {
    let rows = g.uiRows.slice()

    if (s.filters.type !== 'all') {
      rows = rows.filter(r => r.type === s.filters.type)
    }

    if (s.filters.date) {
      const from = String(s.filters.date)
      const to = s.filters.dateTo ? String(s.filters.dateTo) : from
      rows = rows.filter(r => {
        const cd = String(r._raw?.calldate || '').slice(0, 10)
        return cd >= from && cd <= to
      })
    }

    if (s.filters.search) {
      const q = s.filters.search.toLowerCase()
      rows = rows.filter(r =>
        String(r.name || '').toLowerCase().includes(q) ||
        String(r.number || '').toLowerCase().includes(q) ||
        String(r._raw?.clid || '').toLowerCase().includes(q) ||
        String(r._raw?.src || '').toLowerCase().includes(q) ||
        String(r._raw?.dst || '').toLowerCase().includes(q) ||
        String(r._raw?.uniqueid || '').toLowerCase().includes(q) ||
        String(r._raw?.linkedid || '').toLowerCase().includes(q)
      )
    }

    return rows
  },

  totalPages (s, g) {
    return Math.max(1, Math.ceil(g.filteredUiRows.length / s.pageSize))
  },

  pagedUiRows (s, g) {
    const start = (s.currentPage - 1) * s.pageSize
    return g.filteredUiRows.slice(start, start + s.pageSize)
  },

  voicemailUiRows (s, g, rootState) {
    const tz = rootState.auth?.timezone || 'America/Phoenix'
    const rows = Array.isArray(s.voicemail.raw) ? s.voicemail.raw : []
    return rows
      .slice()
      .sort((a, b) => Number(b.id || 0) - Number(a.id || 0))
      .map(vm => mapVoicemailToUi(vm, s.voicemail.audioUrlById[String(vm.id)] || null, tz))
  },

  voicemailSelected (s, g) {
    const id = s.voicemail.selectedId
    if (!id) return null
    return g.voicemailUiRows.find(v => String(v.id) === String(id)) || null
  }
}

const mutations = {
  setScope (s, v) { s.scope = v },

  setLoading (s, v) { s.loading = !!v },
  setError (s, v) { s.error = v || null },
  setMessage (s, v) { s.message = v || null },
  setRaw (s, v) { s.raw = Array.isArray(v) ? v : [] },

  setSearch (s, v) { s.filters.search = v || '' },
  setDate (s, v) { s.filters.date = v || '' },
  setDateTo (s, v) { s.filters.dateTo = v || '' },
  setDateRange (s, { from, to }) { s.filters.date = from || ''; s.filters.dateTo = to || '' },
  setType (s, v) { s.filters.type = v || 'all' },

  setPageSize (s, v) { s.pageSize = Number(v) || 10 },
  setCurrentPage (s, v) { s.currentPage = Number(v) || 1 },

  setVoicemailLoading (s, v) { s.voicemail.loading = !!v },
  setVoicemailError (s, v) { s.voicemail.error = v || null },
  setVoicemailMessage (s, v) { s.voicemail.message = v || null },
  setVoicemailRaw (s, v) { s.voicemail.raw = Array.isArray(v) ? v : [] },
  setVoicemailSelectedId (s, v) { s.voicemail.selectedId = v ? String(v) : null },

  setVoicemailAudioLoading (s, v) { s.voicemail.audioLoading = !!v },

  setVoicemailAudioUrl (s, { id, url }) {
    const key = String(id)
    const prev = s.voicemail.audioUrlById[key]
    if (prev && prev.startsWith('blob:')) {
      try { URL.revokeObjectURL(prev) } catch (e) {}
    }
    s.voicemail.audioUrlById = { ...s.voicemail.audioUrlById, [key]: url }
  },

  updateVoicemailFlagLocal (s, { id, flag }) {
    const key = String(id)
    s.voicemail.raw = (s.voicemail.raw || []).map(r => {
      if (String(r.id) !== key) return r
      return { ...r, flag: flag ?? '' }
    })
  },

  removeVoicemailLocal (s, id) {
    const key = String(id)
    const prev = s.voicemail.audioUrlById[key]
    if (prev && prev.startsWith('blob:')) {
      try { URL.revokeObjectURL(prev) } catch (e) {}
    }
    const copy = { ...s.voicemail.audioUrlById }
    delete copy[key]
    s.voicemail.audioUrlById = copy
    s.voicemail.raw = (s.voicemail.raw || []).filter(r => String(r.id) !== key)
    if (String(s.voicemail.selectedId || '') === key) s.voicemail.selectedId = null
  }
}

const actions = {
  async fetchCdr ({ state, commit }) {
    commit('setLoading', true)
    commit('setError', null)
    commit('setMessage', null)

    try {
      const params = { page: 1, per_page: 200 }

      if (state.filters.date) {
        params.date_from = state.filters.date
        params.date_to = state.filters.dateTo || state.filters.date
      }

      if (state.filters.search) {
        params.q = state.filters.search
      }

      const res = state.scope === 'customer'
        ? await CommunicationsService.listCustomerCdr(params)
        : await CommunicationsService.listUserCdr(params)

      const payload = res?.data || {}
      const rows = Array.isArray(payload.data) ? payload.data : []

      commit('setRaw', rows)
      commit('setMessage', payload.message || null)
      commit('setCurrentPage', 1)
    } catch (e) {
      commit('setRaw', [])
      commit(
        'setError',
        e?.response?.data?.message ||
          JSON.stringify(e?.response?.data?.errors || '') ||
          e?.message ||
          'Failed to load CDRs'
      )
      commit('setCurrentPage', 1)
    } finally {
      commit('setLoading', false)
    }
  },

  async fetchUserVoicemail ({ commit }) {
    commit('setVoicemailLoading', true)
    commit('setVoicemailError', null)
    commit('setVoicemailMessage', null)

    try {
      const res = await CommunicationsService.listUserVoicemail()
      const payload = res?.data || {}
      const rows = Array.isArray(payload.data) ? payload.data : []

      commit('setVoicemailRaw', rows)
      commit('setVoicemailMessage', payload.message || null)
    } catch (e) {
      commit('setVoicemailRaw', [])
      commit(
        'setVoicemailError',
        e?.response?.data?.message ||
          JSON.stringify(e?.response?.data?.errors || '') ||
          e?.message ||
          'Failed to load voicemail'
      )
    } finally {
      commit('setVoicemailLoading', false)
    }
  },

  async selectVoicemail ({ state, commit, dispatch }, id) {
    const key = String(id)
    commit('setVoicemailSelectedId', key)

    const row = (state.voicemail.raw || []).find(r => String(r.id) === key)
    if (!row) return

    if (!isReadFlag(row.flag)) {
      await dispatch('markVoicemailRead', key)
    }

    if (state.voicemail.audioUrlById[key]) return

    commit('setVoicemailAudioLoading', true)
    try {
      const res = await CommunicationsService.getVoicemailRecording(key)
      const blob = res?.data
      if (!blob) return
      const url = URL.createObjectURL(blob)
      commit('setVoicemailAudioUrl', { id: key, url })
    } catch (e) {
      commit(
        'setVoicemailError',
        e?.response?.data?.message ||
          e?.message ||
          'Failed to load voicemail audio'
      )
    } finally {
      commit('setVoicemailAudioLoading', false)
    }
  },

  async markVoicemailRead ({ commit }, id) {
    const key = String(id)
    commit('updateVoicemailFlagLocal', { id: key, flag: 'read' })
    try {
      await CommunicationsService.updateVoicemail(key, { flag: 'read' })
    } catch (e) {}
  },

  async deleteVoicemail ({ commit }, id) {
    const key = String(id)
    commit('removeVoicemailLocal', key)
    try {
      await CommunicationsService.deleteVoicemail(key)
    } catch (e) {}
  },

  async markAllVoicemailRead ({ state, dispatch }) {
    const rows = Array.isArray(state.voicemail.raw) ? state.voicemail.raw : []
    const unread = rows.filter(r => !isReadFlag(r.flag))
    for (const r of unread) {
      await dispatch('markVoicemailRead', r.id)
    }
  }
}

/* ----------------- helpers ----------------- */

function isReadFlag (flag) {
  const f = String(flag || '').toLowerCase()
  return f.includes('read') || f.includes('old')
}

function dedupeCdrRows (rows) {
  const buckets = new Map()

  for (const r of rows) {
    const key = String(r.linkedid || r.uniqueid || r._id || '')
    if (!key) continue
    const list = buckets.get(key) || []
    list.push(r)
    buckets.set(key, list)
  }

  const chosen = []
  for (const [, list] of buckets.entries()) {
    chosen.push(pickBestLeg(list))
  }

  chosen.sort((a, b) => String(b.calldate || '').localeCompare(String(a.calldate || '')))
  return chosen
}

function pickBestLeg (legs) {
  let best = legs[0]
  let bestScore = -1

  for (const r of legs) {
    const disp = String(r.disposition || '').toUpperCase()
    const bill = Number(r.billsec || 0)
    const dur = Number(r.duration || 0)
    const dst = String(r.dst || '').toLowerCase()

    const answered = disp === 'ANSWERED'
    let score = 0
    if (answered) score += 1000
    if (bill > 0) score += 200 + bill
    score += dur

    if (dst === 'sendcall') score -= 10

    if (score > bestScore) {
      bestScore = score
      best = r
    }
  }

  return best
}

function classifyType (r) {
  const disp = String(r.disposition || '').toUpperCase()
  if (['NO ANSWER', 'BUSY', 'FAILED', 'CONGESTION', 'CANCEL'].includes(disp)) return 'Missed'

  const dcontext = String(r.dcontext || '').toLowerCase()
  if (dcontext.includes('outbound')) return 'Outgoing'

  const lastdata = String(r.lastdata || '').toLowerCase()
  if (lastdata.includes('@bandwidth')) return 'Outgoing'

  const src = String(r.src || '')
  const dst = String(r.dst || '')

  if (looksLikeExtension(src) && looksLikeE164(dst)) return 'Outgoing'
  if (looksLikeE164(src) && looksLikeExtension(dst)) return 'Incoming'

  return 'Incoming'
}

function looksLikeExtension (v) {
  const s = String(v || '').trim()
  return /^[0-9]{2,6}$/.test(s)
}

function looksLikeE164 (v) {
  const s = String(v || '').trim()
  return /^\+?[0-9]{10,15}$/.test(s)
}

function extractE164FromLastdata (r) {
  const lastdata = String(r.lastdata || '')
  const m = lastdata.match(/PJSIP\/(\+?\d+)@/i)
  return m?.[1] || null
}

function cleanClidName (clid) {
  const s = String(clid || '')
  const m = s.match(/"""+([^"]+)"""\s*</) || s.match(/"([^"]+)"\s*</)
  return m?.[1]?.trim() || ''
}

function cleanClidNumber (clid) {
  const s = String(clid || '')
  const m = s.match(/<([^>]+)>/)
  return m?.[1]?.replace(/"/g, '')?.trim() || ''
}

function formatDuration (r) {
  const bill = Number(r.billsec || 0)
  const dur = Number(r.duration || 0)
  const sec = bill > 0 ? bill : dur
  if (!sec) return '—'
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`
}

function formatCallTime (calldate, timezone) {
  const isoish = String(calldate || '').replace(' ', 'T') + 'Z' // CDR times are UTC
  const d = new Date(isoish)
  if (Number.isNaN(d.getTime())) return String(calldate || '')
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone || undefined,
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit'
  }).format(d)
}

function mapCdrToUiRow (r, timezone) {
  const type = classifyType(r)
  const icon = type === 'Missed'
    ? XCircleIcon
    : (type === 'Outgoing' ? ArrowUpRightIcon : ArrowDownLeftIcon)

  const outbound = type === 'Outgoing'
  const clidName = cleanClidName(r.clid)
  const clidNum = cleanClidNumber(r.clid)

  const number = outbound
    ? (extractE164FromLastdata(r) || (looksLikeE164(r.dst) ? r.dst : '') || clidNum || '')
    : (r.src || clidNum || '')

  const name = clidName || (type === 'Missed' ? 'Missed Call' : (outbound ? 'Outbound' : 'Inbound'))

  return {
    id: r._id || r.uniqueid || r.linkedid,
    icon,
    name,
    type,
    number,
    time: formatCallTime(r.calldate, timezone),
    duration: formatDuration(r),
    direction: outbound ? 'Outbound' : 'Inbound',
    _raw: r
  }
}

function parseCallerId (callerid) {
  const s = String(callerid || '').trim()
  const m1 = s.match(/"([^"]+)"\s*<([^>]+)>/)
  if (m1) return { name: m1[1].trim(), number: m1[2].trim() }
  const m2 = s.match(/<([^>]+)>/)
  if (m2) return { name: '', number: m2[1].trim() }
  if (s.startsWith('+') || /^\d{7,}$/.test(s)) return { name: '', number: s }
  return { name: s, number: '' }
}

function formatVmDate (origtime, timezone) {
  const t = Number(origtime || 0)
  if (!t) return ''
  const d = new Date(t * 1000)
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone || undefined,
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(d)
}

function formatVmDuration (duration) {
  const sec = Number(duration || 0)
  if (!sec) return '—'
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`
}

function mapVoicemailToUi (vm, audioUrl, timezone) {
  const { name, number } = parseCallerId(vm.callerid)
  const read = isReadFlag(vm.flag)

  return {
    id: vm.id,
    from: name || (number ? number : 'Unknown Caller'),
    number: number || '',
    date: formatVmDate(vm.origtime, timezone),
    duration: formatVmDuration(vm.duration),
    audioUrl: audioUrl || '#',
    transcription: '',
    read,
    _raw: vm
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
